/**
 * 実績トレンド反映ダイアログ専用の live-basis PreviewTrendReflection hook
 * (demand-trend-reflection-single-authority design D2/D5)。
 *
 * ダイアログの「計画に反映」押下 (modal open) 時に、backend へ live editRows
 * (未保存の編集中の値) を per-retailer plan として渡して補正系列を再計算させ、
 * 応答を hold する。modal は編集を block するので open〜apply 間で live plan は
 * 不変 = ダイアログの表示総和と Apply が採用する系列は同一応答由来になり、
 * 「表示される補正後 BG 合計 == Apply 後に保存される BG 合計」が構造的に
 * 成立する (design AC)。
 *
 * useTrendReflectionWindow (main 画面の avgRatio バッジ・破線用、saved draft
 * 基準・W 変更で再計算) とは別の hold-until-apply な状態機械のため独立 hook に
 * 分離する — 同じ RPC を呼ぶが基準 (live vs saved) と生存期間 (dialog open〜
 * apply/cancel vs page 滞在中) が異なる。
 */
import { useCallback, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { previewTrendReflection } from '@ebalance/proto/bffuser/v1/demand_forecast_service-DemandForecastService_connectquery'
import {
  PreviewTrendReflectionRequestSchema,
  RetailerPlanSlotsSchema,
  type TrendReflectionPreview,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'

import { appToaster } from '~/libs/toaster'
import { komaToProtoSlots } from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { RetailerEditRow } from '~/pages/DemandForecastDaily/models/types'

type Params = {
  caseId: string | undefined
  areaCode: AreaCode | undefined
  targetDate: ProtoDate | undefined
  /** 現在の集計窓 W (コマ数)。ダイアログは main 画面と同じ W で再計算する。 */
  windowKoma: number
  /**
   * PreviewTrendReflection が失敗した時に呼ばれる (ダイアログを閉じる導線)。
   * 失敗すると preview が undefined のままで Apply が恒久 disabled・表示値 0 の
   * まま残るため、error toast (本 hook が表示) に加えて呼び出し元がダイアログを
   * 閉じ、ユーザーが再度「計画に反映」で retry できる状態に戻す (sibling の
   * import flow が採る error toast + 再操作の規約に揃える、codex review 指摘)。
   */
  onError: () => void
}

type Result = {
  /** hold している live-basis の trend_preview。未 open / RPC 未応答は undefined。 */
  preview: TrendReflectionPreview | undefined
  /** RPC 発火中かどうか (Apply ボタンの loading / disabled 制御用)。 */
  isPending: boolean
  /**
   * 「計画に反映」押下 (modal open) 時に呼ぶ。引数の editRows を live
   * per-retailer plan として PreviewTrendReflection を発火し、応答を hold する。
   */
  open: (editRows: Record<string, RetailerEditRow>) => void
  /** ダイアログを閉じる (キャンセル / 反映後) 時に呼び、hold した preview を破棄する。 */
  reset: () => void
}

export function useTrendReflectDialogPreview({
  caseId,
  areaCode,
  targetDate,
  windowKoma,
  onError,
}: Params): Result {
  const { t } = useTranslation()
  const [preview, setPreview] = useState<TrendReflectionPreview | undefined>(
    undefined,
  )
  const mutation = useMutation(previewTrendReflection)
  // open / reset ごとに増える世代番号。onSuccess は自分が発火した世代が最新の
  // ときだけ preview へ反映する。cancel(reset) 後や再 open 後に遅延到着した
  // 前世代の応答が stale な系列を hold し、その系列で Apply が有効化される
  // (= 編集中の値と食い違う補正を保存できてしまう) のを防ぐ (codex review 指摘)。
  const generationRef = useRef(0)

  const open = useCallback(
    (editRows: Record<string, RetailerEditRow>) => {
      if (
        caseId === undefined ||
        areaCode === undefined ||
        targetDate === undefined
      )
        return
      // 再 open 時は前回 hold を即破棄し、新応答が来るまで Apply を無効化する
      // (isApplyDisabled = preview === undefined)。
      const generation = ++generationRef.current
      setPreview(undefined)
      const retailerPlanSlots = Object.entries(editRows).map(
        ([retailerBusinessCd, row]) =>
          create(RetailerPlanSlotsSchema, {
            retailerBusinessCd,
            planSlots: komaToProtoSlots(row.forecastSlots),
          }),
      )
      mutation.mutate(
        create(PreviewTrendReflectionRequestSchema, {
          caseId,
          areaCode,
          targetDate,
          aggregationWindowKoma: windowKoma,
          retailerPlanSlots,
        }),
        {
          onSuccess: (resp) => {
            // 前世代 (cancel 済み / 再 open で上書き済み) の遅延応答は捨てる。
            if (generationRef.current !== generation) return
            setPreview(resp.trendPreview)
          },
          onError: () => {
            // 前世代の失敗は無視 (cancel 済み / 再 open 済みのダイアログを閉じない)。
            if (generationRef.current !== generation) return
            appToaster.create({
              type: 'error',
              title: t('page:demandForecastDaily.trendReflect.previewError'),
            })
            onError()
          },
        },
      )
    },
    // mutation は connect-query の useMutation が返す安定 object ではないため
    // deps に含めない (mutate 自体は同一 RPC 定義に対して stable、
    // useTrendReflectionWindow.ts の同パターンに揃える)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseId, areaCode, targetDate, windowKoma, onError, t],
  )

  const reset = useCallback(() => {
    // 世代を進めて進行中リクエストの遅延応答を無効化する。
    generationRef.current++
    setPreview(undefined)
  }, [])

  return { preview, isPending: mutation.isPending, open, reset }
}
